/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/assistantThreadsWorldModelAppendix.ts
 * PURPOSE: Markdown appendix appended to `brazn_world_model_context` so Langflow sees this tab’s
 *          assistant thread memory: other persisted threads plus the active thread transcript.
 */

import type { AiAssistantMessage } from './aiAssistantTypes';
import { loadHistoryIndex, loadThreadBody } from './aiAssistantPersistence';
import { partitionAssistantContent } from './assistantContentPartition';

function clip(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 40))}\n\n_(truncated)_`;
}

function formatThreadMessages(messages: AiAssistantMessage[]): string {
  const lines: string[] = [];
  for (const m of messages) {
    if (m.loading && !m.content.trim()) continue;
    const role = m.role === 'user' ? 'User' : 'Assistant';
    let body =
      m.role === 'assistant'
        ? partitionAssistantContent(m.content).displayBody.trim()
        : m.content.trim();
    if (!body && m.role === 'user') body = '_(empty)_';
    if (!body && m.role === 'assistant') continue;
    const oneLine = body.replace(/\s+/g, ' ').trim();
    lines.push(`- **${role}:** ${oneLine}`);
  }
  return lines.join('\n');
}

/**
 * Bounded markdown for merging into world-model context. Reads past threads from sessionStorage;
 * `messages` should be the active conversation including the in-flight user turn when applicable.
 */
export function buildAssistantThreadsWorldModelAppendix(params: {
  sessionScope: string;
  flowId: string;
  activeThreadId: string;
  messages: AiAssistantMessage[];
  maxTotalChars?: number;
  maxOtherThreadChars?: number;
  maxCurrentThreadChars?: number;
}): string {
  const maxTotal = params.maxTotalChars ?? 14_000;
  const maxOther = params.maxOtherThreadChars ?? 2_400;
  const maxCurrent = params.maxCurrentThreadChars ?? 6_000;

  if (!params.flowId.trim() || !params.sessionScope.trim()) return '';

  const idx = loadHistoryIndex(params.sessionScope, params.flowId);
  const entries = idx?.entries ?? [];

  const intro = [
    '## Langflow assistant — thread memory (this browser tab)',
    '_Recent Langflow chats in this tab (other threads + your current thread). Prefer the user’s latest message as the task; use this for names, decisions, and earlier turns._',
    '',
  ];

  const sections: string[] = [];

  const others = entries.filter((e) => e.threadId !== params.activeThreadId).slice(0, 15);
  for (const e of others) {
    const body = loadThreadBody(params.sessionScope, params.flowId, e.threadId);
    if (!body?.messages.length) continue;
    const md = formatThreadMessages(body.messages);
    if (!md.trim()) continue;
    sections.push(
      [
        `### ${e.preview || 'Thread'}`,
        `_Updated: ${e.updatedAt}_`,
        '',
        clip(md, maxOther),
        '',
        '---',
        '',
      ].join('\n')
    );
  }

  const currentMd = formatThreadMessages(
    params.messages.filter((m) => !m.loading || Boolean(m.content.trim()))
  );
  if (currentMd.trim()) {
    sections.push(['### Current thread (active)', '', clip(currentMd, maxCurrent)].join('\n'));
  }

  if (sections.length === 0) return '';

  const out = [...intro, ...sections].join('\n').trim();
  return clip(out, maxTotal);
}
